/**
 * Générateur — Calculer, appliquer des proportions, des pourcentages.
 * Attendu ID : 982 | Niveau : 5ème | Domaine : proportionnalite
 *
 * ═══════════════════════════════════════════════════════════════════
 * PLAN DE CARTES (version conforme à la spécification 982.json, 2026-09-21)
 * ═══════════════════════════════════════════════════════════════════
 *
 * Libellé officiel (spec.attendu) : « Calculer, appliquer des proportions, des pourcentages. »
 *
 * Catégorie « appliquer_pourcent » (niveau 1 : appliquer un taux donné à un effectif rond,
 * une étape, résultat entier par construction)
 *   - appliquer_pourcent_qcm_unique      QCM : p % de N personnes
 *   - appliquer_pourcent_texte           Saisie : p % de N personnes, réponse entière
 *   - appliquer_pourcent_tableau_vf      V/F : quatre affirmations « p % de N, c'est X »
 *   - appliquer_pourcent_relier          Relier : quatre taux ↔ quatre effectifs, même total
 *   - appliquer_pourcent_texte_trou_drag Trous (drag) : retour à l'unité puis linéarité
 *                                        multiplicative (1 % puis p %)
 *
 * Catégorie « trouver_pourcent » (niveau 2 : calculer le pourcentage d'une part à partir de
 * deux effectifs, total diviseur de 100 ou multiple, résultat entier)
 *   - trouver_pourcent_qcm_unique        QCM : part sur total, énoncé à trois parts
 *   - trouver_pourcent_texte             Saisie : part sur total, réponse en % (suffixe)
 *   - trouver_pourcent_tableau_vf        V/F : quatre affirmations « x sur N, cela fait p % »
 *   - trouver_pourcent_relier            Relier : « x sur N » ↔ « p % »
 *
 * Catégorie « scrutin » (niveau 3 : élection de délégué à quatre candidats, arrondi au
 * dixième annoncé, somme des pourcentages arrondis regardée en face)
 *   - scrutin_texte_trou                 Trous (saisie) : un champ par candidat + la somme
 *   - scrutin_qcm_unique                 QCM : pourcentage de voix d'un candidat
 *   - scrutin_tableau_vf                 V/F : trois affirmations de pourcentage + la somme
 *
 * Catégorie « deux_etapes » (niveau 4 : problème à deux étapes, jamais plus)
 *   - deux_etapes_complement_texte       Saisie : appliquer un taux puis prendre le complément
 *   - deux_etapes_taux_arrondi_texte     Saisie : pourcentage non exact, arrondi au dixième annoncé
 *   - deux_etapes_comparer_qcm_unique    QCM : deux classes de totaux différents, quelle
 *                                        proportion est la plus grande
 *   - deux_etapes_tableau_vf             V/F : pourcentages des deux classes, effectifs et
 *                                        comparaison des proportions
 *
 * ── Spécification appliquée ──
 * Bornes :
 *   - effectif_total_de_la_population : [20, 400] entier. Quand le résultat doit tomber juste,
 *     le total est pris dans {20, 25, 40, 50, 80, 100, 125, 200, 250, 400} (liste de la spec).
 *     Le scrutin travaille sur un nombre de votants de [21, 30] (classe de collège), le total
 *     du niveau 4 non exact sur [20, 400] libre.
 *   - effectif_d_une_part : [1, total − 1], jamais nul, jamais la totalité.
 *   - nombre_de_parts_du_probleme : [2, 5] ; 3 parts pour les énoncés « trouver », 4 pour le
 *     scrutin (valeur retenue par la spec pour le contexte électoral).
 *   - taux_a_appliquer : entier de [1, 90] ; le couple (taux, total) est choisi pour que le
 *     produit soit un entier de personnes.
 *   - arrondi_du_pourcentage_calcule : 0 décimale aux niveaux 1 et 2 (résultat exact, aucune
 *     consigne d'arrondi), 1 décimale aux niveaux 3 et 4 (consigne d'arrondi annoncée).
 *   - somme_des_pourcentages_apres_arrondi : calculée, jamais supposée ; l'affirmation du V/F
 *     et le trou final disent la somme réellement obtenue.
 * Exclusions respectées : aucune quatrième proportionnelle, aucun produit en croix, aucun
 *   coefficient multiplicateur nommé, aucun problème à plus de deux étapes, aucune part nulle
 *   ni totale, aucun produit taux × effectif de personnes non entier, aucune consigne d'arrondi
 *   sur un résultat exact et aucun résultat non exact sans consigne d'arrondi.
 *
 * Erreurs visées → mots-clés (codes des distracteurs et des cartes qui les provoquent) :
 *   1. rendre le quotient décimal pour un pourcentage (0,25 au lieu de 25 %, ou l'inverse :
 *      le facteur 100)                        → `quotient_decimal_pour_pourcentage`
 *   2. diviser par l'effectif d'une part au lieu de l'effectif total
 *                                             → `division_par_la_part`
 *   3. appliquer le taux quand la question porte sur le complément
 *                                             → `complement_oublie`
 *   4. rendre l'effectif quand on demande le pourcentage, ou l'inverse
 *                                             → `effectif_pour_pourcentage`
 *   5. répondre par un nombre non entier de personnes sans s'en étonner
 *                                             → `personnes_non_entier`
 *   6. croire qu'un pourcentage plus élevé signifie un effectif plus élevé
 *                                             → `pourcentage_pris_pour_effectif`
 *   Compléments (familles de distracteurs supplémentaires, hors spec) :
 *      `proportions_egales`, `comparaison_impossible`, `taux_pris_pour_le_dixieme`.
 *
 * Graduation → cartes :
 *   niveau 1 → appliquer_pourcent_qcm_unique, appliquer_pourcent_texte,
 *              appliquer_pourcent_tableau_vf, appliquer_pourcent_relier,
 *              appliquer_pourcent_texte_trou_drag
 *   niveau 2 → trouver_pourcent_qcm_unique, trouver_pourcent_texte,
 *              trouver_pourcent_tableau_vf, trouver_pourcent_relier
 *   niveau 3 → scrutin_texte_trou, scrutin_qcm_unique, scrutin_tableau_vf
 *   niveau 4 → deux_etapes_complement_texte, deux_etapes_taux_arrondi_texte,
 *              deux_etapes_comparer_qcm_unique, deux_etapes_tableau_vf
 *
 * Points non tranchés (voir audit/5eme/982.md) :
 *   - Le payload n'a pas de canal de correction : « la correction affiche au moins deux des
 *     trois procédures » (type_reponse, point de vigilance 5) n'est pas servi par le gen. La
 *     carte à trous matérialise le retour à l'unité puis la linéarité multiplicative dans le
 *     travail de l'élève, c'est tout ce que le format permet.
 *   - « Ne pas faire dépendre la seconde question du résultat de la première » (vigilance 6)
 *     contre « une question finale sur la somme obtenue » (type_reponse, graduation niveau 3) :
 *     la somme est servie, corrigée contre la valeur vraie et non contre les réponses de
 *     l'élève ; les quatre pourcentages restent corrigés indépendamment.
 *
 * Invariants : arithmétique entière (centièmes de pourcentage, dixièmes de pourcentage),
 * bonne réponse unique par construction, boucles bornées avec repli déterministe (jamais de
 * throw), distracteurs distincts de la bonne réponse sous peine de nouveau tirage complet.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 982;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Mots-clés des erreurs visées (spec 982.json) ───
export const ERREURS_VISEES = {
    quotient_decimal_pour_pourcentage: 'Rendre le quotient décimal en guise de pourcentage (0,25 au lieu de 25 %, ou l\'inverse) : l\'erreur se voit à un facteur 100',
    division_par_la_part: 'Diviser par l\'effectif d\'une part au lieu de l\'effectif total quand le problème regroupe plusieurs parts',
    complement_oublie: 'Appliquer directement le taux donné quand la question porte sur le complément',
    effectif_pour_pourcentage: 'Rendre l\'effectif de la part quand la question demande le pourcentage, ou l\'inverse',
    personnes_non_entier: 'Répondre par un nombre non entier de personnes sans s\'en étonner',
    pourcentage_pris_pour_effectif: 'Croire qu\'un pourcentage plus élevé signifie un effectif plus élevé, alors que les deux situations n\'ont pas le même effectif total',
};

export const REGLES_DISTRACTEURS_QCM = {
    ...ERREURS_VISEES,
    proportions_egales: 'Croire que deux situations de totaux différents ont la même proportion (complément)',
    comparaison_impossible: 'Croire que deux effectifs totaux différents interdisent de comparer les proportions (complément)',
    taux_pris_pour_le_dixieme: 'Prendre le dixième du nombre au lieu du taux demandé (complément)',
};

// ═══════════════════════════════════════════════════════════
// HELPERS NUMÉRIQUES (arithmétique entière, affichage français)
// ═══════════════════════════════════════════════════════════

/** Totaux qui divisent 100 ou en sont un multiple (spec, variable effectif_total). */
const TOTAUX_JUSTES = [20, 25, 40, 50, 80, 100, 125, 200, 250, 400];

/** Les taux entiers de [1, 90] dont le produit par N est un entier de personnes. */
const tauxJustesPour = (N) => {
    const out = [];
    for (let p = 1; p <= 90; p++) if ((p * N) % 100 === 0) out.push(p);
    return out;
};

/** Dixièmes → texte français (« 375 » → « 37,5 », « 250 » → « 25 »). */
const dixiemes = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Millièmes → texte français (« 375 » → « 0,375 », « 250 » → « 0,25 »). */
const milliemes = (m) => {
    const ent = Math.floor(m / 1000);
    const dec = String(m % 1000).padStart(3, '0').replace(/0+$/, '');
    return dec ? `${ent},${dec}` : String(ent);
};

/** Arrondi au dixième de pourcentage de part/total, en dixièmes de %, sans flottant. */
const pourcentDixiemes = (part, total) => {
    const n = part * 1000;
    const q = Math.floor(n / total);
    const r = n - q * total;
    return 2 * r >= total ? q + 1 : q;
};

/** Le pourcentage part/total tombe-t-il juste au dixième ? */
const exactAuDixieme = (part, total) => (part * 1000) % total === 0;

/** Quotient décimal d'un pourcentage ENTIER (« 24 » → « 0,24 », « 30 » → « 0,3 »). */
const quotientDecimal = (pourcent) => milliemes(pourcent * 10);

// ═══════════════════════════════════════════════════════════
// CONTEXTES
// ═══════════════════════════════════════════════════════════

/**
 * Situations à une part : un collectif, un fait, la question posée. `deIndividus`
 * porte l'élision (« d'élèves », « de spectateurs ») : jamais d'article recollé
 * devant un nom variable.
 */
const SITUATIONS = [
    { collectif: (N) => `un collège de ${N} élèves`, individus: 'élèves', deIndividus: 'd\'élèves', fait: 'sont demi-pensionnaires', demande: 'Combien d\'élèves sont demi-pensionnaires ?' },
    { collectif: (N) => `un club de ${N} adhérents`, individus: 'adhérents', deIndividus: 'd\'adhérents', fait: 'sont inscrits au tournoi', demande: 'Combien d\'adhérents sont inscrits au tournoi ?' },
    { collectif: (N) => `une salle de ${N} spectateurs`, individus: 'spectateurs', deIndividus: 'de spectateurs', fait: 'ont moins de 18 ans', demande: 'Combien de spectateurs ont moins de 18 ans ?' },
    { collectif: (N) => `un village de ${N} habitants`, individus: 'habitants', deIndividus: 'd\'habitants', fait: 'sont abonnés à la médiathèque', demande: 'Combien d\'habitants sont abonnés à la médiathèque ?' },
    { collectif: (N) => `une association de ${N} adhérents`, individus: 'adhérents', deIndividus: 'd\'adhérents', fait: 'pratiquent la natation', demande: 'Combien d\'adhérents pratiquent la natation ?' },
];

/** Situations à trois parts (pour l'erreur « diviser par une part »). */
const SITUATIONS_PARTS = [
    { collectif: (N) => `Un club compte ${N} adhérents`, individus: 'adhérents', verbe: 'pratiquent', groupes: ['le judo', 'la danse', 'l\'escalade'] },
    { collectif: (N) => `Dans un collège, ${N} élèves choisissent une langue`, individus: 'élèves', verbe: 'ont choisi', groupes: ['l\'allemand', 'l\'espagnol', 'l\'italien'] },
    { collectif: (N) => `Une médiathèque compte ${N} lecteurs`, individus: 'lecteurs', verbe: 'ont emprunté', groupes: ['un roman', 'une bande dessinée', 'un documentaire'] },
];

const PRENOMS = ['Alice', 'Basile', 'Chloé', 'Diego', 'Nora', 'Sami', 'Lina', 'Tom', 'Jade', 'Malo'];

/** Deux groupes comparables, de totaux différents (niveau 4). Tous masculins. */
const CLUBS = ['club de judo', 'club de danse', 'club de natation', 'club d\'escalade', 'club de tennis'];

const FAITS_CLUB = ['sont inscrits au tournoi', 'ont payé la licence', 'participent au stage de printemps'];

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 1 — APPLIQUER UN POURCENTAGE (niveau 1)
// ═══════════════════════════════════════════════════════════

const construireAppliquerQcm = (s, N, p) => {
    const part = (p * N) / 100;
    const complement = ((100 - p) * N) / 100;
    if (!Number.isInteger(part) || !Number.isInteger(complement)) return null;
    const distracteurs = [
        { code: 'quotient_decimal_pour_pourcentage', valeur: String(p * N) },
        { code: 'complement_oublie', valeur: String(complement) },
        { code: 'effectif_pour_pourcentage', valeur: String(p) },
    ];
    const bonne = String(part);
    const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
    if (new Set(valeurs).size !== valeurs.length) return null;

    const payload = makeQcm(
        valeurs, bonne,
        `<p>Dans ${s.collectif(N)}, ${p} % ${s.fait}. ${s.demande}</p>`,
        opts('Appliquer un pourcentage à un effectif'),
    );
    payload._meta.params = { niveau: 1, sens: 'appliquer', total: N, taux: p, part, reponse: bonne, distracteurs };
    return payload;
};

const genAppliquerQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const N = randomChoice(TOTAUX_JUSTES);
        const p = randomChoice(tauxJustesPour(N));
        const payload = construireAppliquerQcm(randomChoice(SITUATIONS), N, p);
        if (payload) return payload;
    }
    return construireAppliquerQcm(SITUATIONS[0], 200, 35);
};

const genAppliquerSaisie = () => {
    let N = 200;
    let p = 35;
    for (let essai = 0; essai < 60; essai++) {
        const n = randomChoice(TOTAUX_JUSTES);
        const t = randomChoice(tauxJustesPour(n));
        if ((t * n) / 100 >= 1) { N = n; p = t; break; }
    }
    const s = randomChoice(SITUATIONS);
    const part = (p * N) / 100;
    const payload = makeSaisie(
        String(part),
        `<p>Dans ${s.collectif(N)}, ${p} % ${s.fait}. ${s.demande}</p>`
        + '<p>La réponse est un nombre entier de personnes.</p>',
        { ...opts('Appliquer un pourcentage, réponse en effectif'), suffixe: s.individus },
    );
    // Carte qui provoque l'erreur 5 : l'élève qui se trompe de calcul obtient un
    // nombre de personnes non entier, et la consigne l'invite à s'en étonner.
    payload._meta.params = { niveau: 1, sens: 'appliquer', total: N, taux: p, part, reponse: String(part), erreurVisee: 'personnes_non_entier' };
    return payload;
};

const genAppliquerVF = () => {
    const s = randomChoice(SITUATIONS);
    const plan = shuffle([true, true, false, false]);
    const lignes = [];
    const bonnes = [];
    const distracteurs = [];
    const vus = new Set();
    for (const vrai of plan) {
        for (let essai = 0; essai < 40; essai++) {
            const N = randomChoice(TOTAUX_JUSTES);
            const p = randomChoice(tauxJustesPour(N));
            const part = (p * N) / 100;
            let valeur = part;
            let code = null;
            if (!vrai) {
                const choixErreur = randInt(0, 2);
                if (choixErreur === 0) { valeur = p * N; code = 'quotient_decimal_pour_pourcentage'; }
                else if (choixErreur === 1) { valeur = ((100 - p) * N) / 100; code = 'complement_oublie'; }
                else { valeur = N / 10; code = 'taux_pris_pour_le_dixieme'; }
                if (!Number.isInteger(valeur) || valeur === part) continue;
            }
            const ligne = `${p} % de ${N} ${s.individus}, c'est ${valeur} ${s.individus}`;
            if (vus.has(ligne)) continue;
            vus.add(ligne);
            lignes.push(ligne);
            bonnes.push(vrai ? 0 : 1);
            if (code) distracteurs.push({ code, valeur: String(valeur) });
            break;
        }
    }
    if (lignes.length < 4) {
        const payload = makeVraiFaux(
            ['10 % de 200 élèves, c\'est 20 élèves', '25 % de 80 élèves, c\'est 20 élèves', '50 % de 400 élèves, c\'est 40 élèves', '20 % de 250 élèves, c\'est 200 élèves'],
            [0, 0, 1, 1],
            '<p>Pour chaque ligne, indique si le calcul est vrai ou faux.</p>',
            opts('Vrai/Faux : appliquer un pourcentage'),
        );
        payload._meta.params = {
            niveau: 1, sens: 'appliquer',
            distracteurs: [{ code: 'taux_pris_pour_le_dixieme', valeur: '40' }, { code: 'complement_oublie', valeur: '200' }],
        };
        return payload;
    }
    const payload = makeVraiFaux(
        lignes, bonnes,
        '<p>Pour chaque ligne, indique si le calcul est vrai ou faux.</p>',
        opts('Vrai/Faux : appliquer un pourcentage'),
    );
    payload._meta.params = { niveau: 1, sens: 'appliquer', distracteurs };
    return payload;
};

const genAppliquerRelier = () => {
    let N = 200;
    let taux = [10, 25, 50, 75];
    for (let essai = 0; essai < 60; essai++) {
        const n = randomChoice(TOTAUX_JUSTES);
        const possibles = tauxJustesPour(n);
        if (possibles.length < 4) continue;
        const t = shuffle([...possibles]).slice(0, 4).sort((a, b) => a - b);
        const parts = t.map((p) => (p * n) / 100);
        if (new Set(parts).size === 4 && parts.every((v) => v >= 1)) { N = n; taux = t; break; }
    }
    const pairs = taux.map((p) => ({ source: `${p} % de ${N}`, target: String((p * N) / 100) }));
    const payload = makeRelier(
        shuffle(pairs),
        '<p>Relie chaque pourcentage au résultat qui lui correspond.</p>',
        opts('Relier un taux et l\'effectif obtenu'),
    );
    payload._meta.params = { niveau: 1, sens: 'appliquer', total: N, taux, erreursVisees: ['effectif_pour_pourcentage'] };
    return payload;
};

const genAppliquerTrou = () => {
    let N = 200;
    let p = 35;
    for (let essai = 0; essai < 60; essai++) {
        // 1 % doit être un entier : seuls les totaux multiples de 100 conviennent.
        const n = randomChoice([100, 200, 400]);
        const possibles = tauxJustesPour(n).filter((t) => t >= 5 && (t * n) / 100 !== n / 100 && (t * n) / 100 !== ((100 - t) * n) / 100);
        if (!possibles.length) continue;
        const t = randomChoice(possibles);
        const un = n / 100;
        const part = (t * n) / 100;
        const complement = ((100 - t) * n) / 100;
        const valeurs = [un, part, complement, t, n / 10];
        if (!Number.isInteger(un) || !Number.isInteger(complement)) continue;
        if (new Set(valeurs.map(String)).size !== valeurs.length) continue;
        N = n; p = t; break;
    }
    const un = N / 100;
    const part = (p * N) / 100;
    const distracteurs = [
        { code: 'complement_oublie', valeur: String(((100 - p) * N) / 100) },
        { code: 'effectif_pour_pourcentage', valeur: String(p) },
        { code: 'taux_pris_pour_le_dixieme', valeur: String(N / 10) },
    ];
    const phrase = `1 % de ${N}, c'est {{${un}}}\ndonc ${p} % de ${N}, c'est {{${part}}}`;
    const payload = makeTexteTrou(
        phrase,
        `<p>Complète le raisonnement en passant par 1 %, puis en multipliant par ${p}.</p>`,
        { ...opts('Retour à l\'unité puis linéarité multiplicative'), mode: 'drag', distracteurs: distracteurs.map((d) => d.valeur) },
    );
    payload._meta.params = { niveau: 1, sens: 'appliquer', total: N, taux: p, un, part, distracteurs };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 2 — TROUVER UN POURCENTAGE (niveau 2)
// ═══════════════════════════════════════════════════════════

/** Tire un total juste et une part dont le pourcentage est un entier de [1, 90]. */
const tirerPartJuste = () => {
    for (let essai = 0; essai < 60; essai++) {
        const T = randomChoice(TOTAUX_JUSTES);
        const p = randomChoice(tauxJustesPour(T));
        const a = (p * T) / 100;
        // Part >= 2 : le fait est ecrit au pluriel (« 2 sont demi-pensionnaires »).
        if (Number.isInteger(a) && a >= 2 && a < T) return { T, a, p };
    }
    return { T: 200, a: 70, p: 35 };
};

const construireTrouverQcm = (s, T, a, p, k) => {
    const b = k * a;
    const c = T - a - b;
    if (c < 2 || b < 2) return null;
    const parLaPart = 100 / k;
    if (!Number.isInteger(parLaPart)) return null;
    const distracteurs = [
        { code: 'quotient_decimal_pour_pourcentage', valeur: `${quotientDecimal(p)} %` },
        { code: 'division_par_la_part', valeur: `${parLaPart} %` },
        { code: 'effectif_pour_pourcentage', valeur: `${a} %` },
    ];
    const bonne = `${p} %`;
    const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
    if (new Set(valeurs).size !== valeurs.length) return null;

    const texte = `<p>${s.collectif(T)}. ${a} ${s.verbe} ${s.groupes[0]}, ${b} ${s.groupes[1]} et ${c} ${s.groupes[2]}.</p>`
        + `<p>Quel pourcentage des ${s.individus} ${s.verbe} ${s.groupes[0]} ?</p>`;
    const payload = makeQcm(valeurs, bonne, texte, opts('Calculer le pourcentage d\'une part'));
    payload._meta.params = { niveau: 2, sens: 'calculer', total: T, part: a, taux: p, parts: [a, b, c], reponse: bonne, distracteurs };
    return payload;
};

const genTrouverQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const { T, a, p } = tirerPartJuste();
        const k = randomChoice([2, 4, 5]);
        const payload = construireTrouverQcm(randomChoice(SITUATIONS_PARTS), T, a, p, k);
        if (payload) return payload;
    }
    return construireTrouverQcm(SITUATIONS_PARTS[0], 100, 10, 10, 2);
};

const genTrouverSaisie = () => {
    const { T, a, p } = tirerPartJuste();
    const s = randomChoice(SITUATIONS);
    const payload = makeSaisie(
        String(p),
        `<p>Dans ${s.collectif(T)}, ${a} ${s.fait}. Quel pourcentage des ${s.individus} cela représente-t-il ?</p>`
        + '<p>Écris seulement le nombre, le symbole % est déjà affiché.</p>',
        { ...opts('Calculer un pourcentage à partir de deux effectifs'), suffixe: '%' },
    );
    payload._meta.params = { niveau: 2, sens: 'calculer', total: T, part: a, taux: p, reponse: String(p), erreurVisee: 'quotient_decimal_pour_pourcentage' };
    return payload;
};

const genTrouverVF = () => {
    const s = randomChoice(SITUATIONS);
    const plan = shuffle([true, true, false, false]);
    const lignes = [];
    const bonnes = [];
    const distracteurs = [];
    const vus = new Set();
    for (const vrai of plan) {
        for (let essai = 0; essai < 40; essai++) {
            const { T, a, p } = tirerPartJuste();
            let affiche = String(p);
            let code = null;
            if (!vrai) {
                const choixErreur = randInt(0, 1);
                if (choixErreur === 0) { affiche = quotientDecimal(p); code = 'quotient_decimal_pour_pourcentage'; }
                else { affiche = String(a); code = 'effectif_pour_pourcentage'; }
                if (affiche === String(p)) continue;
            }
            const ligne = `Sur ${T} ${s.individus}, ${a} ${s.fait}, cela fait ${affiche} %`;
            if (vus.has(ligne)) continue;
            vus.add(ligne);
            lignes.push(ligne);
            bonnes.push(vrai ? 0 : 1);
            if (code) distracteurs.push({ code, valeur: affiche });
            break;
        }
    }
    if (lignes.length < 4) {
        const payload = makeVraiFaux(
            ['Sur 200 élèves, 70 sont demi-pensionnaires, cela fait 35 %', 'Sur 50 élèves, 20 sont demi-pensionnaires, cela fait 40 %', 'Sur 100 élèves, 25 sont demi-pensionnaires, cela fait 0,25 %', 'Sur 400 élèves, 40 sont demi-pensionnaires, cela fait 40 %'],
            [0, 0, 1, 1],
            '<p>Pour chaque ligne, indique si le pourcentage annoncé est vrai ou faux.</p>',
            opts('Vrai/Faux : calculer un pourcentage'),
        );
        payload._meta.params = {
            niveau: 2, sens: 'calculer',
            distracteurs: [{ code: 'quotient_decimal_pour_pourcentage', valeur: '0,25' }, { code: 'effectif_pour_pourcentage', valeur: '40' }],
        };
        return payload;
    }
    const payload = makeVraiFaux(
        lignes, bonnes,
        '<p>Pour chaque ligne, indique si le pourcentage annoncé est vrai ou faux.</p>',
        opts('Vrai/Faux : calculer un pourcentage'),
    );
    payload._meta.params = { niveau: 2, sens: 'calculer', distracteurs };
    return payload;
};

const genTrouverRelier = () => {
    let T = 200;
    let parts = [20, 50, 70, 100];
    for (let essai = 0; essai < 60; essai++) {
        const t = randomChoice(TOTAUX_JUSTES);
        const possibles = tauxJustesPour(t);
        if (possibles.length < 4) continue;
        const taux = shuffle([...possibles]).slice(0, 4);
        const candidats = taux.map((p) => (p * t) / 100);
        if (new Set(candidats).size === 4 && new Set(taux).size === 4 && candidats.every((v) => v >= 1 && v < t)) {
            T = t; parts = candidats.sort((a, b) => a - b); break;
        }
    }
    const pairs = parts.map((a) => ({ source: `${a} sur ${T}`, target: `${(a * 100) / T} %` }));
    const payload = makeRelier(
        shuffle(pairs),
        '<p>Relie chaque proportion au pourcentage qui lui correspond.</p>',
        opts('Relier une proportion et son pourcentage'),
    );
    payload._meta.params = { niveau: 2, sens: 'calculer', total: T, parts, erreursVisees: ['effectif_pour_pourcentage'] };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 3 — SCRUTIN À QUATRE CANDIDATS (niveau 3)
// ═══════════════════════════════════════════════════════════

/**
 * Tire un scrutin : V votants (classe de collège), quatre candidats aux voix
 * distinctes et non nulles, au moins un pourcentage qui ne tombe pas juste au
 * dixième (sans quoi la consigne d'arrondi serait interdite par la spec).
 */
const tirerScrutin = () => {
    for (let essai = 0; essai < 80; essai++) {
        const V = randInt(21, 30);
        const noms = shuffle([...PRENOMS]).slice(0, 4);
        const voix = [];
        let reste = V;
        for (let i = 0; i < 3; i++) {
            const max = reste - (3 - i);
            if (max < 1) break;
            voix.push(randInt(1, Math.min(max, V - 3)));
            reste -= voix[voix.length - 1];
        }
        if (voix.length < 3 || reste < 1) continue;
        voix.push(reste);
        if (new Set(voix).size !== 4) continue;
        if (voix.some((a) => a < 1 || a >= V)) continue;
        if (voix.every((a) => exactAuDixieme(a, V))) continue;
        const classe = V + randInt(0, 4);
        const tenths = voix.map((a) => pourcentDixiemes(a, V));
        if (new Set(tenths).size !== 4) continue;
        return { V, classe, noms, voix, tenths, somme: tenths.reduce((x, y) => x + y, 0) };
    }
    const V = 24;
    const voix = [6, 4, 9, 5];
    const tenths = voix.map((a) => pourcentDixiemes(a, V));
    return { V, classe: 28, noms: ['Alice', 'Basile', 'Chloé', 'Diego'], voix, tenths, somme: tenths.reduce((x, y) => x + y, 0) };
};

/** Phrase d'énoncé commune aux trois cartes de scrutin. */
const enonceScrutin = (sc) => {
    const detail = sc.noms.map((n, i) => `${n} ${sc.voix[i]} voix`).join(', ');
    return `<p>Dans une classe de ${sc.classe} élèves, ${sc.V} ont voté pour élire un délégué. `
        + 'Quatre candidats se présentaient et chaque élève a voté pour un seul candidat. '
        + `Les voix se répartissent ainsi : ${detail}.</p>`;
};

const genScrutinTrou = () => {
    const sc = tirerScrutin();
    const lignes = sc.noms.map((n, i) => `${n} a obtenu {{${dixiemes(sc.tenths[i])}}} % des voix`);
    lignes.push(`Les quatre pourcentages additionnés donnent {{${dixiemes(sc.somme)}}} %`);
    const payload = makeTexteTrou(
        lignes.join('\n'),
        enonceScrutin(sc)
        + '<p>Calcule le pourcentage de voix de chaque candidat, arrondi au dixième, puis additionne les quatre pourcentages obtenus.</p>',
        { ...opts('Pourcentage de voix de chaque candidat'), mode: 'saisie' },
    );
    payload._meta.params = {
        niveau: 3, sens: 'calculer', total: sc.V, classe: sc.classe, parts: sc.voix,
        pourcentages: sc.tenths.map(dixiemes), somme: dixiemes(sc.somme), sommeDixiemes: sc.somme,
        arrondi: 1, erreursVisees: ['quotient_decimal_pour_pourcentage'],
    };
    return payload;
};

const construireScrutinQcm = (sc, i, j) => {
    const a = sc.voix[i];
    const b = sc.voix[j];
    const bonne = `${dixiemes(sc.tenths[i])} %`;
    const distracteurs = [
        { code: 'quotient_decimal_pour_pourcentage', valeur: `${milliemes(sc.tenths[i])} %` },
        { code: 'division_par_la_part', valeur: `${dixiemes(pourcentDixiemes(a, b))} %` },
        { code: 'effectif_pour_pourcentage', valeur: `${a} %` },
    ];
    const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
    if (new Set(valeurs).size !== valeurs.length) return null;
    const payload = makeQcm(
        valeurs, bonne,
        enonceScrutin(sc)
        + `<p>Quel est le pourcentage de voix de ${sc.noms[i]} ? Arrondis au dixième.</p>`,
        opts('Pourcentage de voix d\'un candidat'),
    );
    payload._meta.params = {
        niveau: 3, sens: 'calculer', total: sc.V, classe: sc.classe, parts: sc.voix,
        part: a, reponse: bonne, arrondi: 1, distracteurs,
    };
    return payload;
};

const genScrutinQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const sc = tirerScrutin();
        const i = randInt(0, 3);
        const j = (i + 1 + randInt(0, 2)) % 4;
        const payload = construireScrutinQcm(sc, i, j);
        if (payload) return payload;
    }
    const sc = { V: 24, classe: 28, noms: ['Alice', 'Basile', 'Chloé', 'Diego'], voix: [6, 4, 9, 5], tenths: [6, 4, 9, 5].map((a) => pourcentDixiemes(a, 24)) };
    sc.somme = sc.tenths.reduce((x, y) => x + y, 0);
    return construireScrutinQcm(sc, 2, 0);
};

const genScrutinVF = () => {
    const sc = tirerScrutin();
    const plan = shuffle([true, true, false]);
    const lignes = [];
    const bonnes = [];
    const distracteurs = [];
    const indices = shuffle([0, 1, 2, 3]).slice(0, 3);
    indices.forEach((idx, rang) => {
        const vrai = plan[rang];
        let affiche = dixiemes(sc.tenths[idx]);
        let code = null;
        if (!vrai) {
            if (rang % 2 === 0) {
                affiche = milliemes(sc.tenths[idx]);
                code = 'quotient_decimal_pour_pourcentage';
            } else {
                const autre = sc.voix[(idx + 1) % 4];
                affiche = dixiemes(pourcentDixiemes(sc.voix[idx], autre));
                code = 'division_par_la_part';
            }
            if (affiche === dixiemes(sc.tenths[idx])) {
                affiche = String(sc.voix[idx]);
                code = 'effectif_pour_pourcentage';
            }
        }
        lignes.push(`${sc.noms[idx]} a obtenu ${affiche} % des voix`);
        bonnes.push(vrai ? 0 : 1);
        if (code) distracteurs.push({ code, valeur: affiche });
    });
    lignes.push('Les quatre pourcentages arrondis au dixième donnent 100 % au total');
    bonnes.push(sc.somme === 1000 ? 0 : 1);

    const payload = makeVraiFaux(
        lignes, bonnes,
        enonceScrutin(sc)
        + '<p>Les pourcentages de voix sont arrondis au dixième. Pour chaque ligne, indique si l\'affirmation est vraie ou fausse.</p>',
        opts('Vrai/Faux : pourcentages d\'un scrutin'),
    );
    payload._meta.params = {
        niveau: 3, sens: 'calculer', total: sc.V, classe: sc.classe, parts: sc.voix,
        sommeDixiemes: sc.somme, arrondi: 1, distracteurs,
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 4 — PROBLÈMES À DEUX ÉTAPES (niveau 4)
// ═══════════════════════════════════════════════════════════

const genComplementSaisie = () => {
    let N = 250;
    let p = 36;
    for (let essai = 0; essai < 60; essai++) {
        const n = randomChoice(TOTAUX_JUSTES);
        const possibles = tauxJustesPour(n).filter((t) => t <= 80 && (t * n) / 100 >= 1 && n - (t * n) / 100 >= 1);
        if (!possibles.length) continue;
        N = n; p = randomChoice(possibles); break;
    }
    const s = randomChoice(SITUATIONS);
    const part = (p * N) / 100;
    const reste = N - part;
    const payload = makeSaisie(
        String(reste),
        `<p>Dans ${s.collectif(N)}, ${p} % ${s.fait}. Combien ${s.deIndividus} ne sont pas dans ce cas ?</p>`
        + '<p>La réponse est un nombre entier de personnes.</p>',
        { ...opts('Appliquer un pourcentage puis prendre le complément'), suffixe: s.individus },
    );
    payload._meta.params = {
        niveau: 4, sens: 'appliquer', total: N, taux: p, part, reponse: String(reste),
        erreurVisee: 'complement_oublie',
    };
    return payload;
};

const genTauxArrondiSaisie = () => {
    let T = 283;
    let a = 100;
    for (let essai = 0; essai < 80; essai++) {
        const t = randInt(60, 400);
        const x = randInt(Math.ceil(t / 20), Math.floor((9 * t) / 10));
        if (x < 1 || x >= t) continue;
        if (exactAuDixieme(x, t)) continue;
        T = t; a = x; break;
    }
    const s = randomChoice(SITUATIONS);
    const tenths = pourcentDixiemes(a, T);
    const payload = makeSaisie(
        dixiemes(tenths),
        `<p>Dans ${s.collectif(T)}, ${a} ${s.fait}. Quel pourcentage des ${s.individus} cela représente-t-il ?</p>`
        + '<p>Arrondis au dixième. Écris seulement le nombre, le symbole % est déjà affiché.</p>',
        { ...opts('Calculer un pourcentage, arrondi au dixième'), suffixe: '%' },
    );
    payload._meta.params = {
        niveau: 4, sens: 'calculer', total: T, part: a, reponse: dixiemes(tenths), arrondi: 1,
        erreurVisee: 'quotient_decimal_pour_pourcentage',
    };
    return payload;
};

/**
 * Deux groupes de totaux différents : celui qui compte le PLUS d'adhérents
 * concernés n'est pas celui où la proportion est la plus grande (erreur visée 6).
 */
const tirerDeuxGroupes = () => {
    for (let essai = 0; essai < 80; essai++) {
        const [TA, TB] = shuffle([...TOTAUX_JUSTES]).slice(0, 2);
        if (TA === TB) continue;
        const pa = randomChoice(tauxJustesPour(TA));
        const pb = randomChoice(tauxJustesPour(TB));
        const aA = (pa * TA) / 100;
        const aB = (pb * TB) / 100;
        if (aA < 1 || aB < 1 || aA >= TA || aB >= TB) continue;
        if (pa === pb || aA === aB) continue;
        // Le groupe à la plus forte proportion doit avoir le plus PETIT effectif concerné.
        const fortA = pa > pb;
        if (fortA ? aA >= aB : aB >= aA) continue;
        const noms = shuffle([...CLUBS]).slice(0, 2);
        return {
            fait: randomChoice(FAITS_CLUB),
            groupes: [
                { nom: noms[0], total: TA, part: aA, taux: pa },
                { nom: noms[1], total: TB, part: aB, taux: pb },
            ],
            indexFort: fortA ? 0 : 1,
        };
    }
    return {
        fait: FAITS_CLUB[0],
        groupes: [
            { nom: 'club de judo', total: 25, part: 12, taux: 48 },
            { nom: 'club de danse', total: 40, part: 14, taux: 35 },
        ],
        indexFort: 0,
    };
};

const enonceDeuxGroupes = (d) => {
    const [g1, g2] = d.groupes;
    return `<p>Le ${g1.nom} compte ${g1.total} adhérents, dont ${g1.part} ${d.fait}. `
        + `Le ${g2.nom} compte ${g2.total} adhérents, dont ${g2.part} ${d.fait}.</p>`;
};

const genComparerQcm = () => {
    const d = tirerDeuxGroupes();
    const fort = d.groupes[d.indexFort];
    const faible = d.groupes[1 - d.indexFort];
    const bonne = `C'est au ${fort.nom} que la proportion d'adhérents concernés est la plus grande.`;
    const distracteurs = [
        { code: 'pourcentage_pris_pour_effectif', valeur: `C'est au ${faible.nom} que la proportion d'adhérents concernés est la plus grande.` },
        { code: 'proportions_egales', valeur: 'Les deux proportions sont les mêmes.' },
        { code: 'comparaison_impossible', valeur: 'On ne peut pas comparer, les deux clubs n\'ont pas le même nombre d\'adhérents.' },
    ];
    const payload = makeQcm(
        [bonne, ...distracteurs.map((x) => x.valeur)], bonne,
        enonceDeuxGroupes(d)
        + '<p>Calcule le pourcentage d\'adhérents concernés dans chaque club, puis choisis la bonne conclusion.</p>',
        opts('Comparer deux proportions de totaux différents'),
    );
    payload._meta.params = {
        niveau: 4, sens: 'calculer',
        groupes: d.groupes.map((g) => ({ total: g.total, part: g.part, taux: g.taux })),
        reponse: bonne, distracteurs,
    };
    return payload;
};

const genComparerVF = () => {
    const d = tirerDeuxGroupes();
    const [g1, g2] = d.groupes;
    const fort = d.groupes[d.indexFort];
    const faible = d.groupes[1 - d.indexFort];
    const plusNombreux = g1.part > g2.part ? g1 : g2;
    const moinsNombreux = g1.part > g2.part ? g2 : g1;
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];

    const ligne1 = plan[0]
        ? `Au ${g1.nom}, ${g1.part} adhérents sur ${g1.total}, cela fait ${g1.taux} %`
        : `Au ${g1.nom}, ${g1.part} adhérents sur ${g1.total}, cela fait ${quotientDecimal(g1.taux)} %`;
    if (!plan[0]) distracteurs.push({ code: 'quotient_decimal_pour_pourcentage', valeur: quotientDecimal(g1.taux) });

    const ligne2 = plan[1]
        ? `Au ${g2.nom}, ${g2.part} adhérents sur ${g2.total}, cela fait ${g2.taux} %`
        : `Au ${g2.nom}, ${g2.part} adhérents sur ${g2.total}, cela fait ${g2.part} %`;
    if (!plan[1]) distracteurs.push({ code: 'effectif_pour_pourcentage', valeur: String(g2.part) });

    const ligne3 = plan[2]
        ? `Le ${plusNombreux.nom} compte plus d'adhérents concernés que le ${moinsNombreux.nom}`
        : `Le ${moinsNombreux.nom} compte plus d'adhérents concernés que le ${plusNombreux.nom}`;

    const ligne4 = plan[3]
        ? `C'est au ${fort.nom} que la proportion d'adhérents concernés est la plus grande`
        : `C'est au ${faible.nom} que la proportion d'adhérents concernés est la plus grande`;
    if (!plan[3]) distracteurs.push({ code: 'pourcentage_pris_pour_effectif', valeur: faible.nom });

    const lignes = [ligne1, ligne2, ligne3, ligne4];
    const bonnes = plan.map((v) => (v ? 0 : 1));
    const payload = makeVraiFaux(
        lignes, bonnes,
        enonceDeuxGroupes(d)
        + `<p>Les adhérents concernés sont ceux qui ${d.fait}. Pour chaque ligne, indique si l'affirmation est vraie ou fausse.</p>`,
        opts('Vrai/Faux : comparer deux proportions'),
    );
    payload._meta.params = {
        niveau: 4, sens: 'calculer',
        groupes: d.groupes.map((g) => ({ total: g.total, part: g.part, taux: g.taux })),
        distracteurs,
    };
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    appliquer_pourcent_qcm_unique: genAppliquerQcm,
    appliquer_pourcent_texte: genAppliquerSaisie,
    appliquer_pourcent_tableau_vf: genAppliquerVF,
    appliquer_pourcent_relier: genAppliquerRelier,
    appliquer_pourcent_texte_trou_drag: genAppliquerTrou,

    trouver_pourcent_qcm_unique: genTrouverQcm,
    trouver_pourcent_texte: genTrouverSaisie,
    trouver_pourcent_tableau_vf: genTrouverVF,
    trouver_pourcent_relier: genTrouverRelier,

    scrutin_texte_trou: genScrutinTrou,
    scrutin_qcm_unique: genScrutinQcm,
    scrutin_tableau_vf: genScrutinVF,

    deux_etapes_complement_texte: genComplementSaisie,
    deux_etapes_taux_arrondi_texte: genTauxArrondiSaisie,
    deux_etapes_comparer_qcm_unique: genComparerQcm,
    deux_etapes_tableau_vf: genComparerVF,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', tableau_vf: 'Vrai/Faux', relier: 'Relier',
    texte_trou: 'Trous', texte_trou_drag: 'Trous (glisser)',
    complement_texte: 'Complément (saisie)',
    taux_arrondi_texte: 'Pourcentage arrondi (saisie)',
    comparer_qcm_unique: 'Comparer (QCM)',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'appliquer_pourcent', label: 'Appliquer un pourcentage donné', formatsList: buildFormats('appliquer_pourcent') },
    { id: 'trouver_pourcent', label: 'Calculer un pourcentage à partir de deux effectifs', formatsList: buildFormats('trouver_pourcent') },
    { id: 'scrutin', label: 'Élection de délégué : les quatre pourcentages et leur somme', formatsList: buildFormats('scrutin') },
    { id: 'deux_etapes', label: 'Problèmes à deux étapes', formatsList: buildFormats('deux_etapes') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
